import { forwardRef, useEffect, useState, type FC } from "react";
import { Input, Loader, Modal, SelectPicker, Uploader } from "rsuite";
import { Brand } from "../../../models/inventory/brand";
import { Form, Button } from "rsuite";
import { errorToast, successToast } from "../../../utils/helper-ui";
import { BsCamera } from "react-icons/bs";
import { asyncLocalStorage } from "../../../utils/storage";
import { Company } from "../../../models/company/company";
import { getCompanies } from "../../../services/company/CompanyApi";
interface BrandFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Brand) => void;
  brand?: Brand;
}

const BrandForm: FC<BrandFormProps> = ({ open, onClose, onSubmit, brand }) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | null | undefined
  >(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [data, setData] = useState<Brand>({
    id: "",
    name: "",
    description: "",
    logo: "",
    website: "",
    email: "",
    phone: "",
    address: "",
    contact_person: "",
    contact_position: "",
    contact_title: "",
    contact_note: "",
    registration_number: "",
  });
  const [uploading, setUploading] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    asyncLocalStorage.getItem("token").then((token) => {
      setToken(token);
    });
  }, []);

  const getAllCompanies = async (search?: string) => {
    try {
      const res = await getCompanies({ page: 1, size: 10, search });
      const data = await res.json();
      setCompanies(data.data.items);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setData(
      brand || {
        id: "",
        name: "",
        description: "",
        logo: "",
        website: "",
        email: "",
        phone: "",
        address: "",
        contact_person: "",
        contact_position: "",
        contact_title: "",
        contact_note: "",
        registration_number: "",
      }
    );
    getAllCompanies();
  }, [brand]);
  return (
    <Modal size={"lg"} open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Brand Form</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form className="w-full" layout="horizontal" fluid>
          <div className="form-group">
            <label>Company</label>
            <div className="input">
              <SelectPicker
                block
                data={companies}
                value={selectedCompanyId}
                onChange={(val) => {
                  setSelectedCompanyId(`${val}`);
                }}
                placeholder="Select a company"
                valueKey="id"
                labelKey="name"
                onSearch={(val) => {
                  if (val) {
                    getAllCompanies(val);
                  }
                }}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Name</label>
            <Input
              name="name"
              placeholder="e.g. Nike BRAND Corporation"
              value={data.name}
              onChange={(value) => setData({ ...data, name: value })}
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <Input
              as={"textarea"}
              rows={4}
              name="description"
              value={data.description}
              placeholder="e.g. Nike is a world-renowned sportswear and footwear company."
              onChange={(value) => setData({ ...data, description: value })}
            />
          </div>
          <div className="form-group">
            <label>Logo</label>

            <Uploader
              fileListVisible={false}
              listType="picture"
              accept="image/*"
              action={process.env.REACT_APP_API_URL + "/v1/file/upload"}
              onUpload={(file) => {
                setUploading(true);
              }}
              headers={{
                Authorization: `Bearer ${token}`,
              }}
              onSuccess={(response, file) => {
                setUploading(false);
                successToast("File uploaded successfully");
                setData({ ...data, logo: response.file.url });
              }}
              onError={() => {
                setUploading(false);
                errorToast("Upload failed");
              }}
            >
              <button style={{ width: 150, height: 150 }}>
                {uploading && <Loader backdrop center />}
                {data.logo ? (
                  <img src={data.logo} width="100%" height="100%" />
                ) : (
                  <BsCamera style={{ fontSize: 80 }} />
                )}
              </button>
            </Uploader>
          </div>
          <div className="form-group">
            <label>Website</label>
            <Input
              name="website"
              placeholder="e.g. https://www.nike.com"
              value={data.website}
              onChange={(e) => setData({ ...data, website: e })}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <Input
              name="email"
              type="email"
              placeholder="e.g. info@nike.com"
              value={data.email}
              onChange={(e) => setData({ ...data, email: e })}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <Input
              name="phone"
              placeholder="e.g. +1 503 671 6453"
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e })}
            />
          </div>
          <div className="form-group">
            <label>Address</label>
            <Input
              as={"textarea"}
              rows={5}
              name="address"
              placeholder="e.g. One Bowerman Dr, Beaverton, OR 97005, United States"
              value={data.address}
              onChange={(e) => setData({ ...data, address: e })}
            />
          </div>
          <div className="form-group">
            <label>Contact Person</label>
            <Input
              name="contact_person"
              placeholder="e.g. John Doe"
              value={data.contact_person}
              onChange={(e) => setData({ ...data, contact_person: e })}
            />
          </div>
          <div className="form-group">
            <label>Contact Position</label>
            <Input
              name="contact_position"
              placeholder="e.g. CEO"
              value={data.contact_position}
              onChange={(e) => setData({ ...data, contact_position: e })}
            />
          </div>
          <div className="form-group">
            <label>Contact Title</label>
            <Input
              name="contact_title"
              placeholder="e.g. Mr"
              value={data.contact_title}
              onChange={(e) => setData({ ...data, contact_title: e })}
            />
          </div>
          <div className="form-group">
            <label>Contact Note</label>
            <Input
              name="contact_note"
              placeholder="e.g. Please contact John Doe for any inquiries."
              value={data.contact_note}
              onChange={(e) => setData({ ...data, contact_note: e })}
            />
          </div>
          <div className="form-group">
            <label>Registration Number</label>
            <Input
              name="registration_number"
              placeholder="e.g. 1234567890"
              value={data.registration_number}
              onChange={(e) => setData({ ...data, registration_number: e })}
            />
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              if (selectedCompanyId) {
                data.company_id = selectedCompanyId;
              }
              onSubmit(data!);
              setData({
                id: "",
                name: "",
                description: "",
                logo: "",
                website: "",
                email: "",
                phone: "",
                address: "",
                contact_person: "",
                contact_position: "",
                contact_title: "",
                contact_note: "",
                registration_number: "",
              });
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default BrandForm;
